import { createContext, useContext, useReducer, useMemo } from "react";

const DeedsContext = createContext(null);

const DEFAULT_DEEDS = [
  "Read a page of Qur'an",
  "Make wudu and pray",
  "Take a short walk",
  "Drink water",
  "Help someone",
  "Make dua",
  "Listen to a reminder",
];

const initialDeedsState = {
  dailyDeeds: {
    pool: DEFAULT_DEEDS,
    completed: {},
    log: {},
  },
};

// Ensure deeds state is valid and has no null/undefined values
function validateDeedsState(state) {
  if (!state || typeof state !== 'object') {
    return { ...initialDeedsState };
  }
  return {
    dailyDeeds: {
      pool: Array.isArray(state?.dailyDeeds?.pool) && state.dailyDeeds.pool.length > 0 
        ? state.dailyDeeds.pool 
        : DEFAULT_DEEDS,
      completed: typeof state?.dailyDeeds?.completed === 'object' && state.dailyDeeds.completed !== null
        ? state.dailyDeeds.completed
        : {},
      log: typeof state?.dailyDeeds?.log === 'object' && state.dailyDeeds.log !== null
        ? state.dailyDeeds.log
        : {},
    },
  };
}

function deedsReducer(state, action) {
  switch (action.type) {
    case "TOGGLE_DEED_COMPLETE": {
      const { iso, deedType = "audio", customDeedTitle } = action.payload;
      const currentState = state.dailyDeeds.completed[iso] || false;
      const newState = !currentState;

      let updatedLog = state.dailyDeeds.log || {};
      if (newState) {
        const deedKey = `${iso}-${deedType}`;

        let deedTitle = customDeedTitle || "Completed a deed";
        if (!customDeedTitle) {
          if (deedType === "audio") deedTitle = "Listened to Daily Surah";
          else if (deedType === "asmaul_husna") deedTitle = "Reflected on the 99 Names of Allah";
          else if (deedType === "prayer") deedTitle = "Completed prayer";
          else if (deedType === "reflection") deedTitle = "Wrote a reflection";
        }

        updatedLog = {
          ...updatedLog,
          [deedKey]: {
            deedTitle,
            type: deedType,
            completedAt: new Date().toISOString(),
          },
        };
      } else {
        const deedKey = `${iso}-${deedType}`;
        const { [deedKey]: removed, ...rest } = updatedLog;
        updatedLog = rest;
      }

      return {
        ...state,
        dailyDeeds: {
          ...state.dailyDeeds,
          completed: {
            ...state.dailyDeeds.completed,
            [iso]: newState,
          },
          log: updatedLog,
        },
      };
    }

    case "LOG_DEED": {
      const { iso, deedType, deedTitle } = action.payload;
      const deedKey = `${iso}-${deedType}`;

      return {
        ...state,
        dailyDeeds: {
          ...state.dailyDeeds,
          log: {
            ...state.dailyDeeds.log,
            [deedKey]: {
              deedTitle: deedTitle || "Completed a deed",
              type: deedType,
              completedAt: new Date().toISOString(),
            },
          },
        },
      };
    }

    case "REMOVE_DEED": {
      const { iso, deedType } = action.payload;
      const deedKey = `${iso}-${deedType}`;
      const { [deedKey]: removed, ...rest } = state.dailyDeeds.log;

      return {
        ...state,
        dailyDeeds: {
          ...state.dailyDeeds,
          log: rest,
        },
      };
    }

    case "HYDRATE_DEEDS":
      return action.payload;

    default:
      return state;
  }
}

export function DeedsProvider({ children, initialState = initialDeedsState }) {
  // Validate and sanitize initial state
  const validatedState = useMemo(() => validateDeedsState(initialState), [initialState]);
  const [state, dispatch] = useReducer(deedsReducer, validatedState);

  const today = new Date().toISOString().split("T")[0];

  // Memoize daily deed selection
  const dailyDeed = useMemo(() => {
    const pool = Array.isArray(state?.dailyDeeds?.pool) && state.dailyDeeds.pool.length > 0 
      ? state.dailyDeeds.pool 
      : DEFAULT_DEEDS;
    if (!Array.isArray(pool) || pool.length === 0) return "No deeds available";
    const index = new Date(today).getTime() % pool.length;
    return pool[index] ?? "No deeds available";
  }, [state?.dailyDeeds?.pool, today]);

  // Memoize deed completion check
  const deedCompleted = useMemo(() => {
    return typeof state?.dailyDeeds?.completed?.[today] === 'boolean' 
      ? state.dailyDeeds.completed[today] 
      : false;
  }, [state?.dailyDeeds?.completed, today]);

  const actions = {
    toggleDeedComplete: (deedType = "audio", customDeedTitle = null) => {
      dispatch({
        type: "TOGGLE_DEED_COMPLETE",
        payload: { iso: today, deedType, customDeedTitle },
      });
    },

    logDeed: (deedType, deedTitle) => {
      dispatch({
        type: "LOG_DEED",
        payload: { iso: today, deedType, deedTitle },
      });
    },

    removeDeed: (deedType) => {
      dispatch({
        type: "REMOVE_DEED",
        payload: { iso: today, deedType },
      });
    },

    hydrateDeeds: (savedState) => {
      dispatch({
        type: "HYDRATE_DEEDS",
        payload: savedState,
      });
    },
  };

  // Memoize provider value
  const value = useMemo(
    () => ({
      state,
      actions,
      dailyDeed,
      deedCompleted,
      today,
    }),
    [state, dailyDeed, deedCompleted, today]
  );

  return (
    <DeedsContext.Provider value={value}>{children}</DeedsContext.Provider>
  );
}

export function useDeeds() {
  const context = useContext(DeedsContext);
  if (!context) {
    throw new Error("useDeeds must be used within DeedsProvider");
  }
  return context;
}
